import React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Apple, Chrome, Mail, Lock, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
} from '@/components/ui/Card';
import { useAuthStore } from '@/stores/authStore';
import { buildAuthLink } from '@/lib/auth-attribution';
import {
  captureAttributionFromUrl,
  getAttributionSnapshot,
  trackFunnelEvent,
} from '@/lib/wc-funnel';
import {
  isSocialAuthEnabled,
  SOCIAL_AUTH_LABELS,
  type SocialAuthProvider,
} from '@/lib/social-auth';
import AuthLegalLinks from '@/components/privacy/AuthLegalLinks';

const SOCIAL_PROVIDERS: Array<{
  provider: SocialAuthProvider;
  label: string;
  icon: React.ReactNode;
}> = [
  { provider: 'google', label: 'Continue with Google', icon: <Chrome className="h-4 w-4" /> },
  { provider: 'apple', label: 'Continue with Apple', icon: <Apple className="h-4 w-4" /> },
];

const LoginPage: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [socialLoading, setSocialLoading] = React.useState<SocialAuthProvider | null>(null);
  const [error, setError] = React.useState('');
  const [errorRef, setErrorRef] = React.useState<string | null>(null);

  const { login, loginWithOAuth, isAuthenticated } = useAuthStore();
  const [searchParams] = useSearchParams();
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isWCBlitzHost = hostname === 'wcblitz.com' || hostname.endsWith('.wcblitz.com');
  const defaultRedirect = isWCBlitzHost ? '/wc/knockout' : '/dashboard';
  const redirectTo = searchParams.get('redirect') || defaultRedirect;
  const isWC = redirectTo.startsWith('/wc');
  const registerLink = buildAuthLink('/register', searchParams, redirectTo);
  const enabledSocialProviders = SOCIAL_PROVIDERS.filter(({ provider }) =>
    isSocialAuthEnabled(provider)
  );

  React.useEffect(() => {
    captureAttributionFromUrl(window.location.href);
    void trackFunnelEvent('signin_viewed', {
      surface: isWC ? 'wc' : 'app',
      redirect_to: redirectTo,
    });
  }, [isWC, redirectTo]);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorRef(null);
    setLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      const ref = `BB-${Date.now().toString(36).toUpperCase().slice(-6)}`;
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      const msg = message.toLowerCase();
      console.error(`[${ref}] Login error:`, err);

      // User-fixable errors — clear guidance, no ref
      if (
        msg.includes('invalid') ||
        msg.includes('credentials') ||
        msg.includes('password') ||
        msg.includes('incorrect')
      ) {
        setError('Invalid email or password. Please check and try again.');
      } else if (
        msg.includes('not confirmed') ||
        msg.includes('not verified') ||
        msg.includes('confirm')
      ) {
        setError('Please verify your email first. Check your inbox for a confirmation link.');
      } else if (msg.includes('not found') || msg.includes('no user')) {
        setError('No account found with this email. Want to sign up instead?');
        // System errors — show ref for support
      } else if (msg.includes('rate') || msg.includes('limit') || msg.includes('too many')) {
        setError('Too many login attempts. Please wait a moment and try again.');
        setErrorRef(ref);
      } else if (msg.includes('network') || msg.includes('fetch') || msg.includes('timeout')) {
        setError('Connection issue. Please check your internet and try again.');
        setErrorRef(ref);
      } else {
        setError('Something went wrong signing in. Please try again.');
        setErrorRef(ref);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: SocialAuthProvider) => {
    setError('');
    setErrorRef(null);
    setSocialLoading(provider);

    try {
      void trackFunnelEvent('signin_submitted', {
        surface: isWC ? 'wc' : 'app',
        redirect_to: redirectTo,
        provider,
        method: 'oauth',
      });

      await loginWithOAuth(provider, {
        attribution: getAttributionSnapshot(),
        redirectTo,
        mode: 'signin',
      });
    } catch (err: any) {
      const ref = `BB-${Date.now().toString(36).toUpperCase().slice(-6)}`;
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      const msg = message.toLowerCase();
      const providerName = SOCIAL_AUTH_LABELS[provider];
      console.error(`[${ref}] ${providerName} login error:`, err);

      if (msg.includes('provider') && msg.includes('enabled')) {
        setError(`${providerName} sign-in is not configured yet. Please use email for now.`);
        setErrorRef(null);
      } else if (msg.includes('popup') || msg.includes('cancel')) {
        setError(`${providerName} sign-in was canceled. Please try again.`);
        setErrorRef(null);
      } else {
        setError(`Something went wrong signing in with ${providerName}. Please try again.`);
        setErrorRef(ref);
      }
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <main className="min-h-screen bg-dark-700 flex items-center justify-center px-4 py-12">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary-500/10 rounded-full blur-3xl animate-pulse-fire" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-fire delay-700" />
      </div>

      <div className="relative w-full max-w-md">
        {isWC && (
          <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-center">
            <p className="text-sm text-emerald-400 font-semibold">
              ⚽ Sign in to save your World Cup bracket
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              Your picks are saved locally. Sign in to sync across devices & compete on the
              leaderboard.
            </p>
          </div>
        )}
        <Card className="glass">
          <CardHeader className="text-center">
            {/* Logo */}
            <div className="mx-auto mb-4">
              <Link to="/" className="flex items-center justify-center space-x-2">
                <div className="h-12 w-12 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
                  <span className="text-white font-heading font-bold text-2xl">B</span>
                </div>
              </Link>
            </div>

            <CardTitle className="text-2xl font-heading text-gradient-fire">Welcome Back</CardTitle>
            <CardDescription>
              Sign in to your BracketBlitz account to continue building winning brackets.
            </CardDescription>
          </CardHeader>

          <CardBody>
            {enabledSocialProviders.length > 0 && (
              <div className="mb-6 space-y-3">
                {enabledSocialProviders.map(({ provider, label, icon }) => (
                  <Button
                    key={provider}
                    type="button"
                    variant="outline"
                    className="w-full border-slate-600 bg-slate-900/40 text-white hover:bg-slate-800"
                    size="lg"
                    leftIcon={icon}
                    loading={socialLoading === provider}
                    disabled={loading || !!socialLoading}
                    onClick={() => void handleSocialSignIn(provider)}
                  >
                    {label}
                  </Button>
                ))}
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-slate-500">
                  <div className="h-px flex-1 bg-slate-700" />
                  <span>or</span>
                  <div className="h-px flex-1 bg-slate-700" />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <Input
                type="email"
                label="Email"
                placeholder="Enter your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                leftIcon={<Mail className="h-4 w-4" />}
                autoComplete="email"
                required
                disabled={loading}
              />

              {/* Password */}
              <Input
                type="password"
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                leftIcon={<Lock className="h-4 w-4" />}
                autoComplete="current-password"
                required
                disabled={loading}
              />

              {/* Themed error panel */}
              {error && (
                <div className="relative rounded-xl border border-red-500/30 bg-gradient-to-r from-red-500/10 via-red-500/5 to-orange-500/10 p-4 backdrop-blur-sm">
                  <button
                    type="button"
                    aria-label="Dismiss sign-in issue"
                    onClick={() => {
                      setError('');
                      setErrorRef(null);
                    }}
                    className="absolute top-2 right-2 text-red-400/60 hover:text-red-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-300">Sign In Issue</p>
                      <p className="text-sm text-red-200/80 mt-1">{error}</p>
                      {errorRef && (
                        <p className="text-[11px] text-red-300/40 mt-2 font-mono select-all">
                          Reference: {errorRef}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Forgot password */}
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>

              {/* Submit button */}
              <Button type="submit" className="w-full" size="lg" loading={loading}>
                Sign In
              </Button>
            </form>
          </CardBody>

          <CardFooter>
            <p className="text-sm text-text-secondary text-center w-full">
              Don't have an account?{' '}
              <Link
                to={registerLink}
                className="text-primary-400 hover:text-primary-300 transition-colors font-medium"
              >
                Sign up
              </Link>
            </p>
          </CardFooter>
        </Card>

        <AuthLegalLinks
          backLabel={isWC ? 'Back to World Cup Blitz' : 'Back to home'}
          backTo={isWC ? '/wc' : '/'}
          includesContestRules={isWC}
        />
      </div>
    </main>
  );
};

export { LoginPage };
